/**
 * Capacity pools — services that physically share ONE counted pool.
 *
 * A half-day daycare dog occupies the SAME physical building as a full-day
 * daycare dog, so both must draw from the SINGLE `daycare` CapacityRule.
 * Modeling half-day as its own independent CapacityRule would let full + half
 * TOGETHER exceed the building (e.g. two 40-slot rules → 80 dogs in a 40-dog
 * facility). This map pins the safe invariant instead:
 *
 *   full-day + half-day bookings, counted TOGETHER, are capped by the daycare
 *   pool — never by two independent rules.
 *
 * Keyed AND valued by `ServiceType.name`. #774 made service_types tenant-
 * scoped, but the NAMES stay stable across tenants (every tenant's copy of
 * 'daycare' is called 'daycare') — which is exactly why this map can remain a
 * pure name→name table with no tenant awareness of its own. The name→ID step
 * is where the tenant predicate belongs, and it is applied by both resolvers
 * that consult this map: resolveCapacityPoolIds in booking/prisma-adapter.ts
 * and the pool block in ai-manager/prisma-adapter.ts createBooking. The value
 * is the "pool parent" — the service whose CapacityRule / CapacityOverride
 * define the cap.
 *
 * This is a code-level grouping deliberately (no schema migration): the real
 * long-term home is the ResourceType/ResourceUnit modelling (#323). Until
 * then, every capacity-reading surface (customer availability, AIM
 * create_booking, admin dashboard meters) consults this map so half-day is
 * bookable and cap-consistent everywhere.
 */
export const CAPACITY_POOL_PARENT: Readonly<Record<string, string>> = {
  'daycare-half': 'daycare',
};

/**
 * The service whose CapacityRule / CapacityOverride cap `serviceName`'s pool.
 * A service not in a pool caps itself (identity) — legacy behavior preserved.
 */
export function capacityPoolParent(serviceName: string): string {
  return CAPACITY_POOL_PARENT[serviceName] ?? serviceName;
}

/**
 * Every service whose bookings COUNT against `serviceName`'s pool, including
 * `serviceName` itself. For a pooled service this returns all siblings sharing
 * the same parent (e.g. both `daycare` and `daycare-half`); for an un-pooled
 * service it returns just that service. Order is deterministic (parent first).
 */
export function capacityPoolMembers(serviceName: string): string[] {
  const parent = capacityPoolParent(serviceName);
  const members: string[] = [parent];
  for (const [child, p] of Object.entries(CAPACITY_POOL_PARENT)) {
    if (p === parent && child !== parent) members.push(child);
  }
  return members;
}

/**
 * True when `serviceName` participates in a shared pool — either as a child
 * (it has a parent other than itself) or as a parent with ≥1 child. Callers
 * use this to skip the extra name→id resolution for the common un-pooled case.
 */
export function isPooledService(serviceName: string): boolean {
  return capacityPoolMembers(serviceName).length > 1;
}

/**
 * Ted A6 (#927, 2026-09-08 decision): the FACILITY headcount — the number a
 * tenant compares against its total-on-site limit (HTHD: 60) — counts dogs
 * physically on the floor for the day: daycare (full + half), boarding
 * (every day of the stay, not just the arrival date) and assessments.
 * Grooming appointments and walks/hikes are NOT on the floor in that sense
 * and never count ("obviously it would not include grooming and hiking").
 *
 * Keyed by `ServiceType.name`, like CAPACITY_POOL_PARENT above, and for the
 * same reason: names are stable across tenants, ids are not. Every headcount
 * read (availability snapshot, the in-transaction re-checks) consults this
 * list; a service missing from it is invisible to the facility limit.
 */
export const FACILITY_HEADCOUNT_SERVICE_NAMES: readonly string[] = [
  'daycare',
  'daycare-half',
  'boarding',
  'assessment',
];

export function countsTowardFacilityHeadcount(serviceName: string): boolean {
  return FACILITY_HEADCOUNT_SERVICE_NAMES.includes(serviceName);
}
